import React from "react";
import "./DigitTicker.css";

const GLYPHS = "0123456789";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const randBetween = (min: number, max: number) =>
  min + Math.random() * (max - min);

type DigitTickerProps = {
  text: string;
  /** Min/max gap (ms) between flip bursts — randomized for an organic feel. */
  minGap?: number;
  maxGap?: number;
  className?: string;
};

/**
 * Renders text in a fixed-width monospace font and perpetually flips a random
 * character (occasionally two) to a random digit, then reverts — the "blinking
 * digits" effect (inspired by arcawealth.ai). Timing is randomized so it never
 * feels metronomic. Honors prefers-reduced-motion.
 */
const DigitTicker: React.FC<DigitTickerProps> = ({
  text,
  minGap = 180,
  maxGap = 700,
  className = "",
}) => {
  const eligible = React.useMemo(
    () =>
      text
        .split("")
        .map((ch, i) => (/[a-zA-Z]/.test(ch) ? i : -1))
        .filter((i) => i >= 0),
    [text]
  );

  const [flips, setFlips] = React.useState<Record<number, string>>({});

  React.useEffect(() => {
    if (prefersReducedMotion() || eligible.length === 0) return;

    const timers = new Set<ReturnType<typeof setTimeout>>();
    let next: ReturnType<typeof setTimeout>;

    const flipOne = () => {
      const idx = eligible[Math.floor(Math.random() * eligible.length)];
      const digit = GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
      setFlips((prev) => ({ ...prev, [idx]: digit }));
      const revert = setTimeout(() => {
        setFlips((prev) => {
          const n = { ...prev };
          delete n[idx];
          return n;
        });
        timers.delete(revert);
      }, randBetween(90, 240));
      timers.add(revert);
    };

    const schedule = () => {
      next = setTimeout(() => {
        flipOne();
        // Occasionally fire a second flip for a livelier, less predictable beat.
        if (Math.random() < 0.35) setTimeout(flipOne, randBetween(40, 160));
        schedule();
      }, randBetween(minGap, maxGap));
      timers.add(next);
    };

    schedule();
    return () => {
      clearTimeout(next);
      timers.forEach(clearTimeout);
    };
  }, [eligible, minGap, maxGap]);

  return (
    <span className={`digit-ticker ${className}`.trim()} aria-label={text}>
      {text.split("").map((ch, i) => {
        const flipped = flips[i];
        return (
          <span
            key={i}
            aria-hidden="true"
            className={`digit-ticker__char${
              flipped ? " digit-ticker__char--flipped" : ""
            }`}
          >
            {flipped ?? (ch === " " ? " " : ch)}
          </span>
        );
      })}
    </span>
  );
};

export default DigitTicker;
